/**
 * Sidebar navigation component - Optimized for mobile with bottom navigation
 * Includes iOS-style back button for secondary pages
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/Button';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  Download,
  ChevronLeft,
  LayoutDashboard,
  Settings,
  Flag,
  CreditCard,
  Briefcase,
  Store,
  Building2,
  MapPin,
  Scissors,
  Tag,
  Camera,
  Calendar,
  Ticket,
  ClipboardList,
  Wallet,
  Gift,
  User,
  HelpCircle,
  Crown,
} from 'lucide-react';

// App Store URLs
const APP_STORE_URLS = {
  android: 'https://play.google.com/store/apps/details?id=club.monkeybarber.app',
  ios: 'https://apps.apple.com/es/app/monkeybarber/id6756655968',
};

export function Sidebar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, customClaims, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [isVIP, setIsVIP] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [isStandalone, setIsStandalone] = useState(false);

  // Detect platform and if app is installed
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Check if running as installed PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);
  }, []);

  // Subscribe to user VIP status in real-time
  useEffect(() => {
    if (!user?.id) return;

    const userDocRef = doc(db, 'users', user.id);
    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        setIsVIP(userData.isVIP || false);
      }
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Handle download app click
  const handleDownloadApp = () => {
    const url = platform === 'ios' ? APP_STORE_URLS.ios : APP_STORE_URLS.android;
    window.open(url, '_blank');
  };

  // Close more menu when route changes
  useEffect(() => {
    setMoreMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path: string) => {
    // Exact match always counts
    if (location.pathname === path) return true;

    // Check if this path has child routes in navLinks
    const hasChildInNav = navLinks.some(link =>
      link.path !== path && link.path.startsWith(path + '/')
    );

    // If this path has children in nav, only exact match counts
    // This prevents /admin being active when on /admin/barbers
    if (hasChildInNav) return false;

    // For leaf routes, allow prefix matching for sub-pages
    return location.pathname.startsWith(path + '/');
  };

  const iconClass = "w-5 h-5";
  const navLinks = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: <LayoutDashboard className={iconClass} />, priority: 1 },
    { path: '/admin/overview', label: 'Panel Admin', icon: <Settings className={iconClass} />, roles: ['super_admin'], priority: 10 },
    { path: '/admin/reports', label: t('nav.reports'), icon: <Flag className={iconClass} />, roles: ['super_admin'], priority: 10 },
    { path: '/admin/billing', label: 'Facturación', icon: <CreditCard className={iconClass} />, roles: ['super_admin', 'franchise_owner', 'admin'], priority: 10 },
    { path: '/admin/job-applications', label: 'Candidaturas', icon: <Briefcase className={iconClass} />, roles: ['super_admin', 'franchise_owner', 'admin'], priority: 10 },
    { path: '/admin/franchise-applications', label: 'Franquicias', icon: <Store className={iconClass} />, roles: ['super_admin'], priority: 10 },
    { path: '/franchises', label: t('nav.franchises'), icon: <Building2 className={iconClass} />, roles: ['super_admin', 'franchise_owner'], priority: 10 },
    { path: '/branches', label: t('nav.branches'), icon: <MapPin className={iconClass} />, roles: ['super_admin', 'franchise_owner', 'admin'], priority: 10 },
    { path: '/services', label: t('nav.services'), icon: <Scissors className={iconClass} />, roles: ['super_admin', 'franchise_owner', 'admin'], priority: 10 },
    { path: '/offers', label: t('nav.offers'), icon: <Tag className={iconClass} />, roles: ['super_admin', 'franchise_owner', 'admin'], priority: 10 },
    { path: '/feed', label: t('nav.feed'), icon: <Camera className={iconClass} />, priority: 2 },
    { path: '/appointments', label: t('nav.appointments'), icon: <Calendar className={iconClass} />, roles: ['super_admin', 'franchise_owner', 'admin', 'barber', 'client'], requiresVIP: true, priority: 10 },
    { path: '/queue', label: t('nav.queue'), icon: <Ticket className={iconClass} />, roles: ['super_admin', 'franchise_owner', 'admin'], priority: 2 },
    { path: '/barber-queue', label: t('nav.myQueue'), icon: <Scissors className={iconClass} />, roles: ['barber'], priority: 2 },
    { path: '/barber-history', label: t('nav.myHistory'), icon: <ClipboardList className={iconClass} />, roles: ['barber'], priority: 10 },
    { path: '/barber-payments', label: t('nav.myPayments'), icon: <Wallet className={iconClass} />, roles: ['barber'], priority: 10 },
    { path: '/client-queue', label: t('nav.myTickets'), icon: <Ticket className={iconClass} />, roles: ['client', 'guest'], priority: 2 },
    { path: '/loyalty', label: t('nav.loyalty'), icon: <Gift className={iconClass} />, roles: ['client'], priority: 3 },
    { path: '/help', label: t('nav.help'), icon: <HelpCircle className={iconClass} />, roles: ['client', 'guest'], priority: 5 },
    { path: '/profile', label: t('nav.profile'), icon: <User className={iconClass} />, priority: 4 },
  ];

  // Pages that are considered "main" navigation pages (no back button needed)
  const mainNavPaths = navLinks.map(link => link.path);

  // Check if current page is a secondary page (needs back button)
  const isSecondaryPage = useMemo(() => {
    // If exact match with main nav, it's not secondary
    if (mainNavPaths.includes(location.pathname)) return false;

    // Check if it's a sub-route of a main nav (like /feed/create, /profile/edit, etc.)
    // These are considered secondary pages
    return true;
  }, [location.pathname, mainNavPaths]);

  // Get page title for secondary pages
  const getPageTitle = () => {
    const path = location.pathname;

    // Map of paths to titles
    const titleMap: Record<string, string> = {
      '/take-ticket': t('nav.takeTicket', 'Reservar'),
      '/create-post': t('nav.createPost', 'Nueva Publicación'),
      '/edit-post': t('nav.editPost', 'Editar'),
      '/create-offer': t('nav.createOffer', 'Nueva Oferta'),
      '/edit-offer': t('nav.editOffer', 'Editar Oferta'),
      '/book-appointment': t('nav.bookAppointment', 'Reservar Cita'),
      '/notifications': t('nav.notifications', 'Notificaciones'),
      '/barber-profile': t('nav.barberProfile', 'Perfil Barbero'),
    };

    // Check exact match first
    if (titleMap[path]) return titleMap[path];

    // Check for dynamic routes
    if (path.startsWith('/notifications')) return t('nav.notifications', 'Notificaciones');
    if (path.startsWith('/appointment')) return t('nav.appointments', 'Citas');
    if (path.startsWith('/barber/')) return t('nav.barberProfile', 'Barbero');
    if (path.startsWith('/branch/')) return t('nav.branch', 'Sucursal');
    if (path.startsWith('/franchise/')) return t('nav.franchise', 'Franquicia');
    if (path.startsWith('/service/')) return t('nav.service', 'Servicio');
    if (path.startsWith('/offer/')) return t('nav.offer', 'Oferta');
    if (path.startsWith('/post/')) return t('nav.post', 'Publicación');

    // Default - use the last part of the path
    const parts = path.split('/').filter(Boolean);
    return parts[parts.length - 1] || 'MonkeyBarber';
  };

  const visibleLinks = useMemo(() => navLinks.filter(link => {
    // Always show links without role restrictions
    if (!link.roles) return true;
    if (!customClaims) return true;

    // Check if user has required role
    const hasRequiredRole = link.roles.includes(customClaims.role);

    // For appointments: if requiresVIP is true and user is a client, check VIP status
    if (link.requiresVIP && customClaims.role === 'client') {
      return hasRequiredRole && (isVIP === true || customClaims.isVIP === true);
    }

    return hasRequiredRole;
  }), [customClaims, isVIP, t]);

  // Get bottom nav items (top 4 by priority) for mobile
  const bottomNavItems = useMemo(() => {
    return [...visibleLinks]
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 4);
  }, [visibleLinks]);

  // Get remaining items for "more" menu
  const moreMenuItems = useMemo(() => {
    const bottomPaths = bottomNavItems.map(item => item.path);
    return visibleLinks.filter(link => !bottomPaths.includes(link.path));
  }, [visibleLinks, bottomNavItems]);

  if (!user) return null;

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden">
        <div className="fixed top-0 left-0 right-0 z-40 bg-surface shadow-md" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 28px)' }}>
          <div className="flex items-center justify-between h-11 px-2">
            {/* Left: Back Button (secondary pages) or Language Toggle (main pages) */}
            {isSecondaryPage ? (
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-0 px-1 py-2 -ml-1 text-accent hover:text-accent-hover transition-colors min-w-[70px] min-h-[44px]"
                aria-label={t('common.back', 'Volver')}
              >
                <ChevronLeft className="w-6 h-6" strokeWidth={2.5} />
                <span className="text-base font-medium">{t('common.back', 'Atrás')}</span>
              </button>
            ) : (
              <button
                onClick={toggleLanguage}
                className="p-2.5 rounded-lg text-ink-muted hover:bg-surface-2 transition-colors min-w-[70px] min-h-[44px] flex items-center justify-start"
                title={t('profile.form.language')}
                aria-label={t('profile.form.language')}
              >
                <span className="text-sm font-medium">{i18n.language === 'es' ? '🇪🇸' : '🇬🇧'}</span>
              </button>
            )}

            {/* Center: Page Title (secondary) or Logo + Title (main) */}
            {isSecondaryPage ? (
              <h1 className="absolute left-1/2 transform -translate-x-1/2 text-base font-semibold text-ink truncate max-w-[50%]">
                {getPageTitle()}
              </h1>
            ) : (
              <Link
                to="/dashboard"
                className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-1.5"
              >
                <img
                  src="/logo/logo_monkey.webp"
                  alt="Monkey Barber Logo"
                  className="h-7 w-7 rounded-full object-cover"
                />
                <span className="font-bold text-sm text-ink">
                  MonkeyBarber
                </span>
              </Link>
            )}

            {/* Right: Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg text-ink-muted hover:bg-surface-2 transition-colors min-w-[70px] min-h-[44px] flex items-center justify-end"
              title={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
              aria-label={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-surface border-t border-hairline shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-around h-16 px-2">
          {bottomNavItems.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`
                flex flex-col items-center justify-center flex-1 h-full py-1.5
                transition-colors rounded-lg mx-0.5
                ${isActive(link.path)
                  ? 'text-accent'
                  : 'text-ink-subtle'
                }
              `}
            >
              <span className="mb-0.5 flex items-center justify-center">{link.icon}</span>
              <span className="text-[0.625rem] font-medium truncate max-w-[60px]">{link.label}</span>
            </Link>
          ))}

          {/* More button */}
          {moreMenuItems.length > 0 && (
            <button
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className={`
                flex flex-col items-center justify-center flex-1 h-full py-1.5
                transition-colors rounded-lg mx-0.5
                ${moreMenuOpen
                  ? 'text-accent'
                  : 'text-ink-subtle'
                }
              `}
            >
              <span className="text-xl mb-0.5">•••</span>
              <span className="text-[0.625rem] font-medium">{t('common.more') || 'Más'}</span>
            </button>
          )}
        </div>
      </div>

      {/* More Menu Overlay (Mobile) */}
      {moreMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMoreMenuOpen(false)}
        />
      )}

      {/* More Menu Sheet (Mobile) */}
      <div
        className={`
          lg:hidden fixed left-0 right-0 z-[35]
          bg-surface rounded-t-2xl shadow-2xl
          border-t border-hairline
          transition-all duration-300 ease-out
          ${moreMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}
          max-h-[60vh] overflow-hidden
        `}
        style={{ bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center py-2">
          <div className="w-12 h-1 bg-surface-3 rounded-full" />
        </div>

        {/* User Info */}
        <div className="px-4 py-3 border-b border-hairline flex items-center gap-3">
          <div className="relative">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName}
                className="w-12 h-12 rounded-full object-cover ring-1 ring-hairline"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-surface-3 text-ink-muted flex items-center justify-center font-bold text-lg">
                {user.displayName?.charAt(0).toUpperCase()}
              </div>
            )}
            {(isVIP || customClaims?.isVIP) && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-accent text-on-accent rounded-full flex items-center justify-center">
                <Crown className="w-3 h-3" aria-hidden="true" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-ink truncate">
                {user.displayName}
              </span>
              {(isVIP || customClaims?.isVIP) && (
                <span className="px-2 py-0.5 bg-accent text-on-accent text-xs font-bold rounded-full">
                  VIP
                </span>
              )}
            </div>
            <span className="text-sm text-ink-subtle capitalize">
              {customClaims?.role || 'cliente'}
            </span>
          </div>
        </div>

        {/* Menu Items */}
        <div className="overflow-y-auto max-h-[calc(60vh-120px)] py-2">
          <div className="grid grid-cols-3 gap-2 px-4">
            {moreMenuItems.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMoreMenuOpen(false)}
                className={`
                  flex flex-col items-center justify-center p-3 rounded-xl
                  transition-colors
                  ${isActive(link.path)
                    ? 'bg-accent/10 text-accent'
                    : 'bg-surface-2 text-ink-muted hover:bg-surface-3'
                  }
                `}
              >
                <span className="mb-1 flex items-center justify-center [&>svg]:w-6 [&>svg]:h-6">{link.icon}</span>
                <span className="text-xs font-medium text-center leading-tight">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Download App Button - only show on mobile web (not in standalone mode) */}
          {platform !== 'desktop' && !isStandalone && (
            <div className="px-4 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadApp}
                className="w-full text-accent border-accent/40 hover:bg-accent/20"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('nav.downloadApp', 'Descargar App')}
              </Button>
            </div>
          )}

          {/* Logout Button */}
          <div className="px-4 mt-4 pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full text-danger hover:bg-danger/20"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside
        className={`
          hidden lg:flex lg:flex-col
          fixed top-0 left-0 h-full bg-surface shadow-xl border-r border-hairline z-50
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-20' : 'w-64'}
        `}
      >
        {/* Desktop Logo & Collapse Button */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-hairline">
          {!collapsed && (
            <Link to="/dashboard" className="flex items-center gap-2">
              <img
                src="/logo/logo_monkey.webp"
                alt="Monkey Barber Logo"
                className="h-10 w-10 rounded-full object-cover"
              />
              <span className="font-bold text-lg text-ink">
                MonkeyBarber
              </span>
            </Link>
          )}
          {collapsed && (
            <Link to="/dashboard" className="mx-auto">
              <img
                src="/logo/logo_monkey.webp"
                alt="Monkey Barber Logo"
                className="h-10 w-10 rounded-full object-cover"
              />
            </Link>
          )}
          {!collapsed && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-2.5 rounded-lg text-ink-muted hover:bg-surface-2 hover:text-ink transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={collapsed ? t('nav.expand') : t('nav.collapse')}
              aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Desktop Expand button when collapsed */}
        {collapsed && (
          <div className="flex justify-center py-2 border-b border-hairline">
            <button
              onClick={() => setCollapsed(false)}
              className="p-2.5 rounded-lg text-ink-muted hover:bg-surface-2 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              title={t('nav.expand')}
              aria-label={t('nav.expand')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {visibleLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors
                  ${isActive(link.path)
                    ? 'bg-accent text-on-accent'
                    : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                  }
                  ${collapsed ? 'justify-center' : ''}
                `}
                title={collapsed ? link.label : ''}
              >
                <span className="flex-shrink-0 flex items-center justify-center">{link.icon}</span>
                <span className={collapsed ? 'hidden' : ''}>{link.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* User Profile & Settings */}
        <div className="border-t border-hairline p-4 bg-surface-2">
          {/* User Info */}
          <div className={`flex items-center gap-3 mb-3 ${collapsed ? 'justify-center' : ''}`}>
            <div className="relative">
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-1 ring-hairline"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-surface-3 text-ink-muted flex items-center justify-center font-semibold flex-shrink-0">
                  {user.displayName?.charAt(0).toUpperCase()}
                </div>
              )}
              {(isVIP || customClaims?.isVIP) && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-accent text-on-accent rounded-full flex items-center justify-center" title="Usuario VIP">
                  <Crown className="w-3 h-3" aria-hidden="true" />
                </div>
              )}
            </div>
            <div className={`flex-1 min-w-0 ${collapsed ? 'hidden' : ''}`}>
              <div className="flex items-center gap-2">
                <div className="font-medium text-ink truncate text-sm">
                  {user.displayName}
                </div>
                {(isVIP || customClaims?.isVIP) && (
                  <span className="px-2 py-0.5 bg-accent text-on-accent text-xs font-bold rounded-full shadow-sm">
                    VIP
                  </span>
                )}
              </div>
              <div className="text-ink-subtle text-xs capitalize truncate">
                {customClaims?.role || 'cliente'}
              </div>
            </div>
          </div>

          {/* Theme & Language Switcher */}
          <div className={`flex items-center gap-2 mb-3 ${collapsed ? 'flex-col' : ''}`}>
            <button
              onClick={toggleLanguage}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 min-h-[44px] text-sm font-medium text-ink bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors ${collapsed ? 'w-full' : 'flex-1'}`}
              title={t('profile.form.language')}
              aria-label={t('profile.form.language')}
            >
              <span className="text-lg">🌐</span>
              <span className={collapsed ? 'hidden' : ''}>
                {i18n.language === 'es' ? 'ES' : 'EN'}
              </span>
            </button>
            <button
              onClick={toggleTheme}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 min-h-[44px] text-sm font-medium text-ink bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors ${collapsed ? 'w-full' : ''}`}
              title={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
              aria-label={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
              <span className={collapsed ? 'hidden' : ''}>
                {theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
              </span>
            </button>
          </div>

          {/* Logout Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className={`w-full text-danger hover:bg-danger/20 font-medium ${collapsed ? 'px-2' : ''}`}
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className={`ml-2 ${collapsed ? 'hidden' : ''}`}>
              {t('nav.logout')}
            </span>
          </Button>
        </div>
      </aside>
    </>
  );
}
